import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { fileUrl } from "@/lib/api";

const MIN = 1;
const MAX = 5;

export default function ImageThumb({ attachment: a }) {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const drag = useRef(null);
  const src = fileUrl(a.url);

  const close = () => { setOpen(false); setScale(1); setPos({ x: 0, y: 0 }); };
  const zoomTo = (s) => {
    const n = Math.min(MAX, Math.max(MIN, s));
    setScale(n);
    if (n === MIN) setPos({ x: 0, y: 0 });
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "+" || e.key === "=") setScale((s) => Math.min(MAX, s + 0.5));
      if (e.key === "-") setScale((s) => { const n = Math.max(MIN, s - 0.5); if (n === MIN) setPos({ x: 0, y: 0 }); return n; });
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open]);

  const onWheel = (e) => zoomTo(scale + (e.deltaY < 0 ? 0.25 : -0.25));

  const onPointerDown = (e) => {
    if (e.pointerType !== "mouse" || scale <= 1) return;
    drag.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    setPos({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
  };
  const onPointerUp = () => { drag.current = null; setDragging(false); };

  const stop = (e) => e.stopPropagation();
  const btn = "bg-white/90 hover:bg-white text-slate-900 text-lg font-semibold rounded-lg w-10 h-10 flex items-center justify-center";

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="block" data-testid={`image-${a.id}`}>
        <img src={src} alt={a.filename} className="w-40 h-40 object-cover rounded-lg border border-white/40" />
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4 overflow-hidden" onClick={close} onWheel={onWheel}>
          <button type="button" onClick={close} className="absolute top-4 right-4 text-white text-3xl leading-none px-3 py-1 z-10" aria-label="Fechar">×</button>
          <img
            src={src}
            alt={a.filename}
            draggable={false}
            onClick={stop}
            onDoubleClick={(e) => { e.stopPropagation(); zoomTo(scale > 1 ? 1 : 2.5); }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            className="max-w-full max-h-[80vh] object-contain select-none"
            style={{
              transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
              transition: dragging ? "none" : "transform 0.15s ease-out",
              cursor: scale > 1 ? (dragging ? "grabbing" : "grab") : "zoom-in",
            }}
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10" onClick={stop}>
            <button type="button" className={btn} onClick={() => zoomTo(scale - 0.5)} aria-label="Diminuir">−</button>
            <span className="text-white text-sm w-12 text-center">{Math.round(scale * 100)}%</span>
            <button type="button" className={btn} onClick={() => zoomTo(scale + 0.5)} aria-label="Aumentar">+</button>
            <a href={src} download={a.filename} className="bg-white text-slate-900 text-sm font-medium rounded-lg px-4 h-10 flex items-center">Baixar</a>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
